import React, { HTMLInputTypeAttribute, JSX, useState, useEffect } from "react";
import {
  UseFormRegister,
  FieldValues,
  FieldError,
  Merge,
  FieldErrorsImpl,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";
import { FaEye, FaEyeSlash } from "react-icons/fa6";

interface InputProps {
  label?: string;
  formFieldClassName?: string;
  type?: HTMLInputTypeAttribute | "key-value";
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  defaultChecked?: boolean;
  name?: string;
  register?: UseFormRegister<any>;
  setValue?: UseFormSetValue<any>;
  watch?: UseFormWatch<any>;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string | FieldError | Merge<FieldError, FieldErrorsImpl<any>>;
  className?: string;
  required?: boolean;
  required_asterik?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  isNotEditable?: boolean;
  actionButton?: JSX.Element;
  isIdPhoneLocal?: boolean;
  max?: string;
  min?: string;
}

const FormInput: React.FC<InputProps> = (params) => {
  const [type, setType] = useState<HTMLInputTypeAttribute | "key-value">(
    params.type || "text"
  );

  const isPassword = params.type === "password";
  const isPhoneNumber = [
    "phone_number_tmp",
    "phone_number",
    "whatsapp_number",
  ].includes(params.name || "");

  const isKeyValueType = params.type === "key-value";
  const [keyValuePairs, setKeyValuePairs] = useState<[string, string][]>([]);

  const watchValue = params.watch?.(params.name || "") || {};
  useEffect(() => {
    if (isKeyValueType && typeof watchValue === "object") {
      setKeyValuePairs(Object.entries(watchValue));
    }
  }, [watchValue]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, "");
    if (isPhoneNumber) {
      if (params.isIdPhoneLocal) {
        value = value.replace(/^0+/, "").replace(/^62/, "");
        value = `62${value}`;
      }
      e.target.value = value;
    }
    params.onChange?.(e);
  };

  const handleKeyChange = (index: number, newKey: string) => {
    const updated = [...keyValuePairs];
    const current = updated[index];
    updated[index] = [newKey, current[1]];
    setKeyValuePairs(updated);
    params.setValue?.(params.name!, Object.fromEntries(updated), {
      shouldDirty: true,
    });
  };

  const handleValueChange = (index: number, newValue: string) => {
    const updated = [...keyValuePairs];
    const current = updated[index];
    updated[index] = [current[0], newValue];
    setKeyValuePairs(updated);
    params.setValue?.(params.name!, Object.fromEntries(updated), {
      shouldDirty: true,
    });
  };

  const handleAddField = () => {
    const updated = [...keyValuePairs, ["", ""] as [string, string]];
    setKeyValuePairs(updated);
    params.setValue?.(params.name!, Object.fromEntries(updated), {
      shouldDirty: true,
    });
  };

  const handleRemoveField = (index: number) => {
    const updated = keyValuePairs.filter((_, i) => i !== index);
    setKeyValuePairs(updated);
    params.setValue?.(params.name!, Object.fromEntries(updated), {
      shouldDirty: true,
    });
  };

  const defaultValue = isPhoneNumber ? "" : params.defaultValue || "";

  return (
    <div
      className={`w-full max-lg:flex-col flex lg:items-center gap-2 ${params.formFieldClassName}`}
    >
      {params.label && (
        <label className="block text-[0.6rem] form-label font-medium w-full lg:w-[200px]">
          {params.required || params.required_asterik ? (
            <span className="text-error">*</span>
          ) : (
            ""
          )}{" "}
          {params.label}
        </label>
      )}

      <div className="flex flex-col flex-1 gap-2 form-input">
        {!params.isNotEditable ? (
          <>
            {isKeyValueType ? (
              <div className="space-y-2">
                {keyValuePairs.map(([key, val], idx) => (
                  <div key={idx} className="flex gap-2 items-center">
                    <input
                      type="text"
                      placeholder="Key"
                      className="input input-sm input-bordered w-1/2"
                      value={key}
                      onChange={(e) => handleKeyChange(idx, e.target.value)}
                      disabled={params.disabled}
                    />
                    <input
                      type="text"
                      placeholder="Value"
                      className="input input-sm input-bordered w-1/2"
                      value={val}
                      onChange={(e) => handleValueChange(idx, e.target.value)}
                      disabled={params.disabled}
                    />
                    {!params.disabled && (
                      <button
                        type="button"
                        className="btn btn-sm btn-error"
                        onClick={() => handleRemoveField(idx)}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                {!params.disabled && (
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={handleAddField}
                  >
                    + Add Field
                  </button>
                )}
              </div>
            ) : params.type === "checkbox" ? (
              <input
                type="checkbox"
                defaultChecked={params.defaultChecked}
                required={params.required}
                readOnly={params.readOnly}
                disabled={params.disabled}
                {...params.register?.(params.name!, {
                  onChange: handleChange,
                })}
                className={`checkbox checkbox-${
                  params.size || "sm"
                } checkbox-bordered checkbox-primary ${
                  params.error ? "border-error" : ""
                } ${params.className}`}
              />
            ) : (
              <div className="relative w-full">
                <input
                  max={params.max ?? ""}
                  min={params.min ?? ""}
                  type={type}
                  placeholder={params.placeholder}
                  required={params.required}
                  inputMode={isPhoneNumber ? "tel" : "text"}
                  defaultValue={defaultValue}
                  readOnly={params.readOnly}
                  disabled={params.disabled}
                  {...params.register?.(params.name!, {
                    onChange: handleChange,
                  })}
                  className={`input ${isPassword ? "is-password" : ""} input-${
                    params.size || "sm"
                  } font-bold placeholder:font-light text-primary input-bordered input-primary ${
                    params.error ? "border-error" : ""
                  } ${params.className} ${
                    !params.type
                      ? "input-xs input-sm input-md input-lg input-xl"
                      : ""
                  } w-full read-only:opacity-50`}
                />

                {params.type === "password" && (
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
                    <button
                      type="button"
                      className="btn btn-ghost btn-xs"
                      onClick={() =>
                        setType((prev) =>
                          prev === "password" ? "text" : "password"
                        )
                      }
                    >
                      {type === "password" ? <FaEye /> : <FaEyeSlash />}
                    </button>
                  </div>
                )}

                {params.actionButton && (
                  <div className="absolute right-2 flex items-center gap-2">
                    {params.actionButton}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <span className="text-xs italic text-accent">
            {params.value || "-"}
          </span>
        )}

        {params.error && (
          <p className="text-error text-xs mt-1">{params.error.toString()}</p>
        )}
      </div>
    </div>
  );
};

export default FormInput;
